import { CustomNode } from '@/types/node-types';
import { CustomEdge } from '@/types/edge-types';
import {
  isArtifactEdge,
  isArtifactNode,
} from '@/pages/workflow/helper/artifact-node-manager';

/**
 * Snapshot of the workflow state taken at pause time + helpers to
 * compute the set of nodes the user materially edited while paused.
 *
 * The backend expects ``edited_node_ids`` to identify the nodes
 * whose cached results must be invalidated on resume.  It then walks
 * the DAG forward from those nodes and deletes every NodeResult row
 * in the closure.  The FE's job is to surface "what semantic config
 * actually changed" so we don't over- or under-invalidate:
 *
 *   * Over-invalidation (false positive)  -> the user re-pays for
 *     work that didn't need to redo, slowing them down.
 *   * Under-invalidation (false negative) -> the resumed run uses
 *     stale upstream values, producing incorrect output.
 *
 * False negatives are far worse than false positives, so the diff
 * is intentionally conservative: any change to a node's params or
 * its incoming edges marks it as edited.  Display-only fields
 * (display_name, info, icon_color, position) do NOT contribute --
 * they don't affect the node's output and shouldn't trigger a
 * recompute.
 */

/** Subset of node state the diff cares about, per react_flow_generated_id. */
interface NodeFingerprint {
  /** Sorted JSON of the params array, with display-only fields stripped. */
  params: string;
  /** Stable JSON of incoming edges (sorted by source/target/port). */
  incomingEdges: string;
  /** Whether the node currently exists in the payload. */
  present: true;
}

export interface PauseSnapshot {
  /** Per-node fingerprint at pause time. */
  fingerprints: Record<string, NodeFingerprint>;
  /** ISO timestamp the snapshot was taken; surfaced in logs only. */
  takenAt: string;
}

/**
 * Param-dict keys that are purely presentational or UI state.  Match
 * the backend's _IGNORED_PARAM_KEYS in temporal/utils/cache.py so
 * the FE's "edited" decision can't drift from the BE's
 * cache-invalidation decision.
 */
const PRESENTATIONAL_PARAM_KEYS = new Set<string>([
  'display_name',
  'info',
  'placeholder',
  'icon',
  'color',
  'bg_color',
  'icon_color',
  'category',
  'display_modal',
  'show',
  'beta',
  'user_config_name',
  'user_config_description',
  // Result-only fields that are written by execution, not editable
  // by the user.  Including them would mark every completed node as
  // "edited" the moment the user paused.
  'trace',
  'status',
]);

function stripPresentational(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(stripPresentational);
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (!PRESENTATIONAL_PARAM_KEYS.has(k)) {
        out[k] = stripPresentational(v);
      }
    }
    return out;
  }
  return value;
}

function stableStringify(value: unknown): string {
  // Stable JSON: object keys sorted lexicographically so two
  // semantically-identical objects produce byte-identical strings.
  // Arrays keep their order (it matters for params + connections).
  return JSON.stringify(value, (_k, v) => {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      return Object.keys(v as Record<string, unknown>)
        .sort()
        .reduce<Record<string, unknown>>((acc, key) => {
          acc[key] = (v as Record<string, unknown>)[key];
          return acc;
        }, {});
    }
    return v;
  });
}

function fingerprintParams(node: CustomNode): string {
  const params = node.data?.struct?.params ?? [];
  return stableStringify(stripPresentational(params));
}

function fingerprintIncomingEdges(nodeId: string, edges: CustomEdge[]): string {
  // Only edges TARGETING this node matter for its execution.  Sort by
  // (source, sourceHandle, targetHandle) so the order is stable
  // regardless of how the user added them.
  const incoming = edges
    .filter((e) => e.target === nodeId)
    .map((e) => ({
      source: e.source,
      sourceHandle: e.sourceHandle ?? null,
      targetHandle: e.targetHandle ?? null,
      type: (e as { edge_type?: string }).edge_type ?? null,
    }))
    .sort((a, b) => {
      const ka = `${a.source}|${a.sourceHandle}|${a.targetHandle}`;
      const kb = `${b.source}|${b.sourceHandle}|${b.targetHandle}`;
      return ka < kb ? -1 : ka > kb ? 1 : 0;
    });
  return stableStringify(incoming);
}

/** Capture the node + edge state at pause time. Artifact nodes/edges are excluded — they're display-only and not part of the DAG. */
export function takePauseSnapshot(
  nodes: CustomNode[],
  edges: CustomEdge[],
): PauseSnapshot {
  const realEdges = edges.filter((e) => !isArtifactEdge(e));
  const fingerprints: Record<string, NodeFingerprint> = {};
  for (const node of nodes) {
    if (isArtifactNode(node)) continue;
    fingerprints[node.id] = {
      params: fingerprintParams(node),
      incomingEdges: fingerprintIncomingEdges(node.id, realEdges),
      present: true,
    };
  }
  return {
    fingerprints,
    takenAt: new Date().toISOString(),
  };
}

/**
 * Returns node IDs the user edited between snapshot and now:
 * params changed, incoming edges changed, node added, or node deleted.
 */
export function computeEditedNodeIds(
  snapshot: PauseSnapshot,
  currentNodes: CustomNode[],
  currentEdges: CustomEdge[],
): string[] {
  const edited = new Set<string>();
  const seenInCurrent = new Set<string>();

  // Exclude artifact nodes/edges: they're frontend-only display constructs
  // and must not influence the DAG-level edited-node diff.
  const realNodes = currentNodes.filter((n) => !isArtifactNode(n));
  const realEdges = currentEdges.filter((e) => !isArtifactEdge(e));

  for (const node of realNodes) {
    seenInCurrent.add(node.id);
    const before = snapshot.fingerprints[node.id];
    if (!before) {
      // Newly added node.  No cached result existed; still mark as
      // edited so the backend has a complete invalidation set if
      // this node wires into something previously completed.
      edited.add(node.id);
      continue;
    }
    const paramsAfter = fingerprintParams(node);
    const edgesAfter = fingerprintIncomingEdges(node.id, realEdges);
    if (paramsAfter !== before.params || edgesAfter !== before.incomingEdges) {
      edited.add(node.id);
    }
  }

  // Nodes that existed at pause time but are gone now -- treat as
  // edited so their downstream invalidates correctly.
  for (const id of Object.keys(snapshot.fingerprints)) {
    if (!seenInCurrent.has(id)) {
      edited.add(id);
    }
  }

  return Array.from(edited).sort();
}

/**
 * Convenience: did the user touch anything since the snapshot?
 * Used to decide whether the "Resume" button should label as
 * "Resume with changes" vs plain "Resume".
 */
export function hasEditsSinceSnapshot(
  snapshot: PauseSnapshot,
  currentNodes: CustomNode[],
  currentEdges: CustomEdge[],
): boolean {
  return computeEditedNodeIds(snapshot, currentNodes, currentEdges).length > 0;
}
